<template>
	<div class="yqf-login-page">
		<div class="container">
			<div class="yqf-login-wrap">
				<div class="login-intro">
					<!-- 左侧文案沿用主站偏商务差旅的表达方式。 -->
					<div class="intro-badge">一起飞账户中心</div>
					<h1>登录后即可继续预订、查单与管理常用信息</h1>
					<p>
						支持验证码登录与账号密码登录，登录成功后将自动回到你进入登录前的页面。
					</p>
					<div class="intro-points">
						<div class="point-item">
							<strong>机票</strong>
							<span>订单与出行状态实时查看</span>
						</div>
						<div class="point-item">
							<strong>酒店</strong>
							<span>统一管理联系人与常用旅客</span>
						</div>
						<div class="point-item">
							<strong>会员中心</strong>
							<span>支持多账号选择后进入对应账户</span>
						</div>
					</div>
					<div class="intro-service">
						<span>24小时服务热线</span>
						<strong>400-678-6622</strong>
					</div>
				</div>
				<div class="login-card">
					<div class="card-head">
						<h2>欢迎登录</h2>
						<p>请输入登录信息，完成后将自动返回来源页面。</p>
					</div>
					<!-- 使用标签页切换两种登录方式，符合你的需求。 -->
					<el-tabs v-model="activeTab" stretch class="login-tabs">
						<el-tab-pane label="验证码登录" name="sms">
							<el-form
								ref="smsForm"
								:model="smsForm"
								label-position="top"
								class="login-form"
								@submit.native.prevent
							>
								<!-- 表单项显式保留 label，避免只依赖 placeholder。 -->
								<el-form-item label="手机号">
									<el-input
										v-model.trim="smsForm.loginMobile"
										maxlength="11"
										placeholder="请输入手机号"
									/>
								</el-form-item>
								<el-form-item label="验证码">
									<div class="code-row">
										<el-input
											v-model.trim="smsForm.loginVCode"
											maxlength="6"
											placeholder="请输入验证码"
											@keyup.enter.native="submitSmsLogin"
										/>
										<el-button
											class="code-btn"
											:disabled="countdown > 0 || sendingCode"
											:loading="sendingCode"
											@click="handleSendCode"
										>
											{{ codeButtonText }}
										</el-button>
									</div>
								</el-form-item>
								<div class="login-helper-tip">
									若您的手机号未注册，登录成功后将为您直接注册。
								</div>
								<el-form-item v-if="requireCaptcha" label="图形验证码">
									<!-- 连续输错 3 次后才展示图形验证码输入。 -->
									<div class="captcha-row">
										<el-input
											v-model.trim="smsForm.imageCaptcha"
											maxlength="4"
											placeholder="请输入图形验证码"
											@keyup.enter.native="submitSmsLogin"
										/>
										<div class="captcha-preview" @click="refreshCaptchaCode">
											{{ captchaCode }}
										</div>
									</div>
									<div class="captcha-tip">已连续输错 3 次，点击右侧验证码可刷新。</div>
								</el-form-item>
								<el-button
									type="danger"
									class="submit-btn"
									:loading="submitting"
									@click="submitSmsLogin"
								>
									验证码登录
								</el-button>
							</el-form>
						</el-tab-pane>
						<el-tab-pane label="账号密码登录" name="password">
							<el-form
								ref="pwdForm"
								:model="pwdForm"
								label-position="top"
								class="login-form"
								@submit.native.prevent
							>
								<el-form-item label="账号">
									<el-input
										v-model.trim="pwdForm.logintext"
										placeholder="请输入账号或手机号"
										@keyup.enter.native="submitPasswordLogin"
									/>
								</el-form-item>
								<el-form-item label="密码">
									<el-input
										v-model.trim="pwdForm.loginpwd"
										type="password"
										show-password
										placeholder="请输入密码"
										@keyup.enter.native="submitPasswordLogin"
									/>
								</el-form-item>
								<el-form-item v-if="requireCaptcha" label="图形验证码">
									<!-- 账号密码登录与短信登录共用同一套验证码规则。 -->
									<div class="captcha-row">
										<el-input
											v-model.trim="pwdForm.imageCaptcha"
											maxlength="4"
											placeholder="请输入图形验证码"
											@keyup.enter.native="submitPasswordLogin"
										/>
										<div class="captcha-preview" @click="refreshCaptchaCode">
											{{ captchaCode }}
										</div>
									</div>
									<div class="captcha-tip">已连续输错 3 次，点击右侧验证码可刷新。</div>
								</el-form-item>
								<el-button
									type="danger"
									class="submit-btn"
									:loading="submitting"
									@click="submitPasswordLogin"
								>
									账号密码登录
								</el-button>
							</el-form>
						</el-tab-pane>
					</el-tabs>
					<div :class="['agreement-box', { 'agreement-box--error': agreementError }]">
						<!-- 登录前强制勾选协议，减少后续合规风险。 -->
						<el-checkbox v-model="agreeProtocol">
							我已阅读并同意
							<a
								class="agreement-link"
								href="https://3g.yiqifei.com/h5/#/packagePublic/pages/YQFCause/user"
								target="_blank"
								rel="noopener noreferrer"
								@click.stop
							>
								服务协议
							</a>
							和
							<a
								class="agreement-link"
								href="https://3g.yiqifei.com/h5/#/packagePublic/pages/YQFCause/yinsi"
								target="_blank"
								rel="noopener noreferrer"
								@click.stop
							>
								隐私政策
							</a>
						</el-checkbox>
						<div v-if="agreementError" class="agreement-error-text">{{ agreementError }}</div>
					</div>
				</div>
			</div>
		</div>

		<!-- 如果后端返回多个账号，则先让用户明确选择后再完成登录。 -->
		<el-dialog
			title="请选择登录账号"
			:visible.sync="accountDialogVisible"
			width="520px"
			:close-on-click-modal="false"
			:close-on-press-escape="false"
		>
			<div class="account-dialog-body">
				<p class="dialog-desc">检测到当前登录信息可对应多个账号，请选择要进入的账号。</p>
				<el-radio-group v-model="selectedAccountCode" class="account-options">
					<div
						v-for="item in accountOptions"
						:key="item.code"
						class="account-option"
					>
						<el-radio :label="item.code" class="account-radio">
							<span class="account-option__text">
								<span class="account-option__title">{{ item.title }}</span>
								<span class="account-option__desc">{{ item.desc }}</span>
							</span>
						</el-radio>
					</div>
				</el-radio-group>
			</div>
			<span slot="footer" class="dialog-footer">
				<el-button class="confirm-login-btn" type="danger" :loading="submitting" @click="confirmAccountLogin">确认登录</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import { getAccountInfo, setAccountInfo } from '@/utils/localdatas';
import { AccountLogin, GetLoginByUserCode } from '@/api/account';
import { SendBandSMSCode } from '@/api/yqfApi';
import { sanitizeReturnUrl, redirectToSafeReturnUrl } from '@/utils/util';

export default {
	name: 'YqfLogin',
	data() {
		return {
			// 统一保存回跳地址，默认回首页。
			returnurl: '/',
			// 标签页名称与登录类型一一对应，便于提交参数。
				activeTab: 'password',
			// 验证码登录表单。
			smsForm: {
				loginMobile: '',
				loginVCode: '',
				imageCaptcha: ''
			},
			// 账号密码登录表单。
			pwdForm: {
				logintext: '',
				loginpwd: '',
				imageCaptcha: ''
			},
			// 提交态避免重复点击。
			submitting: false,
			// 发码态单独控制按钮 loading。
			sendingCode: false,
			// 倒计时用于限制重复发送验证码。
			countdown: 0,
			timer: null,
			// 多账号选择弹窗状态。
			accountDialogVisible: false,
			accountOptions: [],
			selectedAccountCode: '',
			// 登录前必须勾选协议确认。
			agreeProtocol: false,
			// 协议未勾选时展示页内错误提示，避免反复弹窗打断输入。
			agreementError: '',
			// 连续登录失败次数达到阈值后启用图形验证码。
			failedLoginCount: 0,
			// 当前图形验证码内容，前端本地生成即可满足防误触需求。
			captchaCode: ''
		};
	},
	computed: {
		// 根据倒计时动态展示按钮文案。
		codeButtonText() {
			return this.countdown > 0 ? this.countdown + 's后重发' : '获取验证码';
		},
		// 连续失败 3 次后才要求输入图形验证码。
		requireCaptcha() {
			return this.failedLoginCount >= 3;
		}
	},
	watch: {
		agreeProtocol(value) {
			// 勾选协议后立即清空错误提示，减少不必要的干扰。
			if (value) {
				this.agreementError = '';
			}
		}
	},
	created() {
		// 初始化回跳地址，兼容 query 中传入的编码字符串。
		this.returnurl = this.parseReturnUrl(this.$route.query.returnurl);

		// 如果已经登录，则不重复停留在登录页，直接回跳。
		if (getAccountInfo()) {
			this.redirectToReturnUrl();
		}
	},
	beforeDestroy() {
		// 页面销毁时清理定时器，避免内存泄漏。
		this.clearTimer();
	},
	methods: {
		// 解析回跳地址，异常时回退首页。
		parseReturnUrl(value) {
			// 登录页只接受本站相对路径、hash 路由或同源绝对地址。
			return sanitizeReturnUrl(value, '/');
		},
		// 简单校验国内手机号，避免无效请求。
		isValidMobile(mobile) {
			return /^1\d{10}$/.test(mobile);
		},
		// 发送验证码前先做必要校验。
		handleSendCode() {
			if (!this.isValidMobile(this.smsForm.loginMobile)) {
				this.$alert('请输入正确的手机号');
				return;
			}

			this.sendingCode = true;

			// 验证码接口改为复用 yqfApi 中的现有方法。
			SendBandSMSCode(
				{
					loginMobile: this.smsForm.loginMobile,
					// 发短信验证码不要求登录态。
					isneedlogin: false,
					areaCode: '',
					loginCode: ''
				},
				(result) => {
					this.sendingCode = false;

					if (result.code === 0) {
						this.$message.success('验证码已发送');
						this.startCountdown();
						return;
					}

					this.$alert(result.msg || '验证码发送失败，请稍后重试');
				}
			);
		},
		// 提交验证码登录，使用后端约定的 logintype=1。
		submitSmsLogin() {
			if (!this.isValidMobile(this.smsForm.loginMobile)) {
				this.$alert('请输入正确的手机号');
				return;
			}

			if (!this.smsForm.loginVCode) {
				this.$alert('请输入验证码');
				return;
			}

			// 两种登录方式共用协议勾选校验，未勾选则禁止继续提交。
			if (!this.validateAgreement()) {
				return;
			}

			// 连续输错超过阈值后，必须先通过图形验证码校验。
			if (!this.validateCaptchaBeforeLogin(this.smsForm.imageCaptcha)) {
				return;
			}

			this.submitLogin({
				logintype: '1',
				loginMobile: this.smsForm.loginMobile,
				loginVCode: this.smsForm.loginVCode
			});
		},
		// 提交账号密码登录，使用后端默认普通登录 logintype=2。
		submitPasswordLogin() {
			if (!this.pwdForm.logintext) {
				this.$alert('请输入账号');
				return;
			}

			if (!this.pwdForm.loginpwd) {
				this.$alert('请输入密码');
				return;
			}

			// 两种登录方式共用协议勾选校验，未勾选则禁止继续提交。
			if (!this.validateAgreement()) {
				return;
			}

			// 连续输错超过阈值后，必须先通过图形验证码校验。
			if (!this.validateCaptchaBeforeLogin(this.pwdForm.imageCaptcha)) {
				return;
			}

			this.submitLogin({
				logintype: '2',
				logintext: this.pwdForm.logintext,
				loginpwd: this.pwdForm.loginpwd
			});
		},
		// 统一封装登录提交，减少两种登录方式的重复代码。
		submitLogin(parm) {
			if (this.submitting) {
				return;
			}

			this.submitting = true;

			AccountLogin(parm, (result) => {
				this.submitting = false;

				if (result.code === 0) {
					this.handleLoginResult(result.data || {});
					return;
				}

				// 登录失败时累计次数，达到阈值后启用图形验证码保护。
				this.handleLoginFailure(result.msg || '登录失败，请稍后重试');
			});
		},
		validateAgreement() {
			// 登录提交前必须显式同意服务协议与隐私政策。
			if (this.agreeProtocol) {
				this.agreementError = '';
				return true;
			}

			this.agreementError = '请先阅读并同意服务协议和隐私政策';
			return false;
		},
		// 连续失败达到阈值后，验证当前输入的图形验证码。
		validateCaptchaBeforeLogin(inputValue) {
			if (!this.requireCaptcha) {
				return true;
			}

			if (!inputValue) {
				this.$alert('连续输错 3 次后，请先输入图形验证码');
				return false;
			}

			if (String(inputValue).toUpperCase() !== this.captchaCode) {
				this.clearCaptchaInputs();
				this.refreshCaptchaCode();
				this.$alert('图形验证码错误，请重新输入');
				return false;
			}

			return true;
		},
		// 登录失败后统一处理错误次数和验证码展示。
		handleLoginFailure(message) {
			this.failedLoginCount += 1;

			if (this.requireCaptcha) {
				// 只要接口返回失败且当前已启用图形验证码，就强制刷新一张新的验证码。
				this.refreshCaptchaCode();
			}

			if (this.requireCaptcha) {
				this.clearCaptchaInputs();
				this.$alert(message + '，已连续输错 3 次，请先输入图形验证码后再试');
				return;
			}

			this.$alert(message);
		},
		// 生成新的 4 位图形验证码，避开易混淆字符。
		refreshCaptchaCode() {
			const source = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
			let nextCode = '';

			for (let index = 0; index < 4; index += 1) {
				nextCode += source.charAt(Math.floor(Math.random() * source.length));
			}

			this.captchaCode = nextCode;
		},
		// 图形验证码失败或成功后都需要清空输入框。
		clearCaptchaInputs() {
			this.smsForm.imageCaptcha = '';
			this.pwdForm.imageCaptcha = '';
		},
		// 登录保护状态在成功后恢复，避免影响后续正常登录。
		resetLoginGuard() {
			this.failedLoginCount = 0;
			this.captchaCode = '';
			this.clearCaptchaInputs();
		},
		// 登录成功后优先判断是否需要多账号二次选择。
		handleLoginResult(data) {
			const accountOptions = this.normalizeAccountList(data.accountlist);

			if (accountOptions.length > 0) {
				this.accountOptions = accountOptions;
				this.selectedAccountCode = accountOptions[0].code;
				this.accountDialogVisible = true;
				return;
			}

			if (data.accountinfo) {
				// 接口同级 accessToken 优先，兼容挂在账号对象上的字段。
				this.finishLogin(data.accountinfo, this.resolveAccessToken(data));
				return;
			}

			this.$alert('登录成功，但没有获取到账号信息，请稍后重试');
		},
		// 确认具体账号后再调用 GetLoginByUserCode 完成最终登录。
		confirmAccountLogin() {
			if (!this.selectedAccountCode) {
				this.$alert('请选择要登录的账号');
				return;
			}

			if (this.submitting) {
				return;
			}

			this.submitting = true;

			GetLoginByUserCode(
				{
					code: this.selectedAccountCode
				},
				(result) => {
					this.submitting = false;

					if (result.code === 0 && result.data && result.data.accountinfo) {
						// 多账号确认登录同样把 accessToken 带回跳地址。
						this.finishLogin(result.data.accountinfo, this.resolveAccessToken(result.data));
						return;
					}

					this.$alert(result.msg || '账号登录失败，请稍后重试');
				}
			);
		},
		// 从登录结果中解析 accessToken，兼容大小写字段名。
		resolveAccessToken(data) {
			if (!data) {
				return '';
			}

			return data.accessToken || data.AccessToken
				|| (data.accountinfo && (data.accountinfo.accessToken || data.accountinfo.AccessToken))
				|| '';
		},
		// 登录完成后统一写入本地并执行回跳。
		finishLogin(accountinfo, accessToken) {
			// 登录成功后立即清空失败计数和验证码状态。
			this.resetLoginGuard();
			setAccountInfo(accountinfo);
			this.accountDialogVisible = false;
			this.$message.success('登录成功');
			// 回跳 URL 追加 token=accessToken，供来源页继续使用登录态。
			this.redirectToReturnUrl(accessToken);
		},
		// 兼容绝对地址、hash 地址和站内 path 三种回跳场景。
		redirectToReturnUrl(accessToken) {
			// 统一复用工具层白名单回跳，防止开放重定向。
			redirectToSafeReturnUrl(this.returnurl, this.$router, accessToken);
		},
		// 后端账号列表字段未明确，做一层兼容映射，尽量稳妥展示。
		normalizeAccountList(accountlist) {
			if (!Array.isArray(accountlist)) {
				return [];
			}

			return accountlist
				.map((item) => {
					const code = this.getAccountCode(item);

					if (!code) {
						return null;
					}

					return {
						code: code,
						title: this.getAccountTitle(item, code),
						desc: this.getAccountDesc(item, code)
					};
				})
				.filter(Boolean);
		},
		// 优先读取常见账号字段名，兼容不同返回结构。
		getAccountCode(item) {
			if (!item) {
				return '';
			}

			return item.code || item.Code || item.Account || item.AccountNo || item.AccountCode || item.UserCode || item.usercode || item.useraccountno || '';
		},
		// 标题优先显示后端返回的 name，并按组织类型补充身份说明。
		getAccountTitle(item, code) {
			const baseName = item.name || item.Name || item.AccountName || item.UserName || item.RealName || item.CompanyName || code;
			return baseName + this.getAccountRoleSuffix(item);
		},
		// 副标题去掉账号编码，只保留组织维度信息，避免直接展示账号。
		getAccountDesc(item, code) {
			const descList = [
				item.CompanyName,
				item.DepartmentName
			].filter(Boolean);

			return descList.length > 0 ? descList.join(' / ') : '请选择该账号继续登录';
		},
		// 组织类型为 INCU 时追加营业员标记。
		getAccountRoleSuffix(item) {
			return item && item.OrganizaType === 'INCU' ? ' -营业员' : '';
		},
		// 开始验证码倒计时，复用主站常见 60 秒规则。
		startCountdown() {
			this.clearTimer();
			this.countdown = 60;

			this.timer = setInterval(() => {
				if (this.countdown > 0) {
					this.countdown -= 1;
					return;
				}

				this.clearTimer();
			}, 1000);
		},
		// 清理定时器时顺便重置引用。
		clearTimer() {
			if (this.timer) {
				clearInterval(this.timer);
				this.timer = null;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/* 页面背景延续主站浅色底与红色品牌强调。 */
.yqf-login-page {
	min-height: 680px;
	padding: 48px 0 72px;
	background: linear-gradient(180deg, #f7f8fb 0%, #fff4f4 100%);
}

/* 主体采用双栏布局，左侧说明右侧表单。 */
.yqf-login-wrap {
	display: flex;
	align-items: stretch;
	gap: 24px;
}

.login-intro,
.login-card {
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 12px 30px rgba(34, 34, 34, 0.08);
}

.login-intro {
	flex: 1;
	padding: 42px 40px;
	border: 1px solid #f2e5e5;

	h1 {
		margin: 16px 0 18px;
		font-size: 34px;
		line-height: 48px;
		color: #1f2937;
	}

	p {
		margin: 0;
		font-size: 16px;
		line-height: 28px;
		color: #6b7280;
	}
}

.intro-badge {
	display: inline-flex;
	align-items: center;
	height: 32px;
	padding: 0 14px;
	border-radius: 16px;
	background: rgba(228, 58, 61, 0.08);
	color: #d73b3e;
	font-size: 14px;
	font-weight: 600;
}

.intro-points {
	margin-top: 34px;
}

.point-item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	min-height: 88px;
	padding: 0 22px;
	margin-bottom: 14px;
	border-radius: 10px;
	background: #fafafa;
	border: 1px solid #f0f0f0;

	strong {
		margin-bottom: 8px;
		font-size: 18px;
		color: #303133;
	}

	span {
		font-size: 14px;
		color: #606266;
	}
}

.intro-service {
	display: flex;
	align-items: baseline;
	gap: 12px;
	margin-top: 28px;
	padding-top: 24px;
	border-top: 1px dashed #ebeef5;

	span {
		font-size: 14px;
		color: #909399;
	}

	strong {
		font-size: 30px;
		color: #e43a3d;
		letter-spacing: 1px;
	}
}

.login-card {
	width: 420px;
	padding: 34px 32px 30px;
	border: 1px solid #f5dddd;
}

.card-head {
	margin-bottom: 18px;

	h2 {
		margin: 0 0 10px;
		font-size: 28px;
		color: #303133;
	}

	p {
		margin: 0;
		font-size: 14px;
		line-height: 24px;
		color: #909399;
	}
}

.login-tabs {
	margin-top: 10px;
}

.login-form {
	padding-top: 10px;
}

.login-helper-tip {
	margin: -2px 0 14px;
	font-size: 12px;
	line-height: 20px;
	color: #909399;
}

.code-row {
	display: flex;
	gap: 12px;
}

.code-btn {
	flex: 0 0 122px;
	padding: 12px 0;
	border-color: #f3c1c2;
	color: #d73b3e;
	background: #fff7f7;
}

.captcha-row {
	display: flex;
	gap: 12px;
}

.captcha-preview {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 122px;
	border: 1px solid #f3c1c2;
	border-radius: 4px;
	background: linear-gradient(135deg, #fff7f7 0%, #fff 100%);
	color: #d73b3e;
	font-size: 20px;
	font-weight: 700;
	letter-spacing: 4px;
	cursor: pointer;
	user-select: none;
}

.captcha-tip {
	margin-top: 8px;
	font-size: 12px;
	line-height: 18px;
	color: #909399;
}

.submit-btn {
	width: 100%;
	margin-top: 8px;
}

.agreement-box {
	margin-top: 18px;
	padding-top: 16px;
	border-top: 1px solid #f1f3f5;
	font-size: 13px;
	line-height: 22px;
	color: #606266;

	/* 协议区整体做成轻量信息块，视觉更完整。 */
	::v-deep .el-checkbox {
		display: flex;
		align-items: flex-start;
		line-height: 22px;
	}

	::v-deep .el-checkbox__label {
		padding-left: 8px;
		line-height: 22px;
		white-space: normal;
		color: #606266;
	}
}

.agreement-box--error {
	padding: 14px 12px 0;
	border: 1px solid rgba(228, 58, 61, 0.18);
	border-radius: 10px;
	background: rgba(228, 58, 61, 0.04);
}

.agreement-link {
	color: #d73b3e;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
}

.agreement-error-text {
	margin: 8px 0 0 24px;
	font-size: 12px;
	line-height: 18px;
	color: #d73b3e;
}

.login-tips {
	margin-top: 18px;
	padding-top: 16px;
	border-top: 1px solid #f1f3f5;
	font-size: 13px;
	line-height: 22px;
	color: #909399;
}

.account-dialog-body {
	padding-top: 4px;
}

.dialog-desc {
	margin: 0 0 16px;
	font-size: 14px;
	line-height: 24px;
	color: #606266;
}

.account-options {
	display: block;
}

.account-option {
	padding: 14px 16px;
	margin-bottom: 12px;
	border: 1px solid #ebeef5;
	border-radius: 10px;
	background: #fafafa;
}

/* 深度覆盖 Element 单选框布局，让圆点与文本顶部对齐。 */
::v-deep .account-radio {
	display: flex;
	align-items: flex-start;
	width: 100%;
	margin-right: 0;
}

/* 单选圆点单独占位，避免被两行文本挤偏。 */
::v-deep .account-radio .el-radio__input {
	display: inline-flex;
	align-items: center;
	padding-top: 2px;
}

/* 文本区域改成块级弹性容器，和圆点保持同排。 */
::v-deep .account-radio .el-radio__label {
	display: flex;
	flex: 1;
	padding-left: 12px;
	white-space: normal;
}

.account-option__text {
	display: block;
	width: 100%;
}

.account-option__title,
.account-option__desc {
	display: block;
}

.account-option__title {
	display: block;
	margin-bottom: 6px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 15px;
	font-weight: 600;
	color: #303133;
}

.account-option__desc {
	font-size: 13px;
	line-height: 20px;
	color: #909399;
}

.dialog-footer {
	display: flex;
	justify-content: center;
	padding-top: 8px;
}

.confirm-login-btn {
	min-width: 160px;
}

/* 在中小屏时切成单栏，避免表单过窄。 */
@media (max-width: 1280px) {
	.yqf-login-wrap {
		flex-direction: column;
	}

	.login-card {
		width: 100%;
	}
}
</style>
